import { Link } from "@inertiajs/react"
import { FileText } from "lucide-react"
import { ScreenHeader } from "@/components/ui/headers"
import { Button } from "@/components/ui/button"
import { formatDateTime } from "@/lib/formatters"
import { formatMoney, parseDecimal } from "./money"
import type { BillDetailed } from "./types"
import { t } from "@/lib/i18n"

type ReceiptScreenProps = {
  bill: BillDetailed
}

/**
 * A record of what was spent, not a confirmation of having spent it. Every way
 * into this screen is navigation — the receipt history and the farewell link —
 * so the celebratory framing it used to carry ("payment received", a "Done"
 * button home) announced an event that had not just happened, and read as a
 * broken step to a diner who was only looking something up. Confirming a
 * payment belongs on the path the payment actually returns to.
 */
export function ReceiptScreen({ bill }: ReceiptScreenProps) {
  // Read, never derived. `User::BillSerializer` owns all three figures, and the
  // fallbacks that used to sit here ("total or else subtotal + tip") were a
  // second opinion on the arithmetic that could only ever disagree with the
  // first — which is how the tip went missing: the backend answered with a
  // non-zero consumption total, so the fallback that would have added the tip
  // never ran.
  const subtotal = parseDecimal(bill.subtotal)
  const tip = parseDecimal(bill.tip)
  // Optional on the type so a fixture written before pickup fees existed still
  // renders; absent reads as none, which is what most bills are.
  const serviceFee = parseDecimal(bill.serviceFee ?? "0")
  const total = parseDecimal(bill.total)
  // Not parsed and not summed — see the block that renders them. Absent on any
  // bill settled before tax configuration existed, which is why it defaults.
  const taxLines = bill.taxLines ?? []

  return (
    <div className="text-foreground" data-testid="receipt-screen-view">
      <ScreenHeader
        title={t("ui.react.features.user.receipt_screen.title")}
      />

      {/* No inner max-width: the header spans the layout's content column, so a
          narrower card underneath would sit visibly off its left edge on desktop.
          Every other diner screen fills the column at `px-4` gutters. */}
      <div className="w-full px-4 pb-8 pt-4">
        <div className="rounded-2xl border bg-card p-5 shadow-xs">
          {/* Letterhead: who, where, when. The date is the restaurant's local
              hour, so a receipt read on a trip still names the evening the diner
              remembers. It is absent while nothing has settled rather than
              rendered as a dash, because an unpaid bill has no payment to date. */}
          <div className="mb-3 border-b border-dashed pb-3 text-xs text-muted-foreground">
            <strong className="font-semibold text-foreground">{bill.locationName}</strong>
            {bill.tableName ? ` · ${t("ui.user.common.table_label", { name: bill.tableName })}` : ""}
            {bill.paidAt && (
              <div className="mt-0.5 tabular-nums" data-testid="receipt-paid-at">
                {t("ui.react.features.user.receipt_screen.paid_on", {
                  date: formatDateTime(bill.paidAt, { timeZone: bill.timeZone }),
                })}
              </div>
            )}
          </div>

          {bill.orders.map((order) => (
            <div key={order.id} className="py-1.5 text-sm">
              <div className="flex justify-between gap-3">
                <span className="min-w-0 truncate text-muted-foreground">
                  <span className="tabular-nums">{order.quantity}×</span> {order.name}
                </span>
                <span className="shrink-0 font-medium tabular-nums">
                  {formatMoney(order.lineTotal, { currency: bill.currency })}
                </span>
              </div>
              {/* The reduction, named. Without it the figure above is simply
                  lower than the menu price with nothing saying why — and for a
                  promotion that gap is the whole thing the restaurant paid for. */}
              {Number(order.discountAmount ?? 0) > 0 && (
                <div
                  className="flex justify-between gap-3 pl-4 text-xs text-muted-foreground"
                  data-testid={`receipt-line-discount-${order.id}`}
                >
                  <span className="min-w-0 truncate">
                    {order.discountLabel || t("ui.react.features.user.order_line.discount")}
                  </span>
                  <span className="shrink-0 tabular-nums">
                    −{formatMoney(order.discountAmount ?? 0, { currency: bill.currency })}
                  </span>
                </div>
              )}
            </div>
          ))}

          <div className="mt-3 border-t border-dashed pt-3">
            <SummaryLine
              label={t("ui.react.features.user.receipt_screen.subtotal")}
              value={subtotal}
              currency={bill.currency}
            />
            {serviceFee > 0 && (
              <SummaryLine
                label={t("ui.react.features.user.receipt_screen.service_fee")}
                value={serviceFee}
                currency={bill.currency}
              />
            )}
            {tip > 0 && (
              <SummaryLine
                label={t("ui.react.features.user.cart_screen.tip")}
                value={tip}
                currency={bill.currency}
              />
            )}
            <div className="mt-2 flex items-baseline justify-between border-t pt-2.5 font-semibold">
              <span className="text-sm">{t("ui.react.features.user.receipt_screen.total")}</span>
              <span className="text-lg tabular-nums">
                {formatMoney(total, { currency: bill.currency })}
              </span>
            </div>

            {/* What was already inside the figure above, not another charge.
                It sits below the total rather than among the addends for
                exactly that reason: a Mexican menu price contains its tax, so
                these rows decompose the total instead of building it, and
                listing them beside subtotal and tip would print a receipt whose
                lines no longer add up.

                `amount` is rendered as the server sent it. `formatMoney` only
                puts a currency symbol on it — nothing here multiplies `rate` by
                anything, because a browser-float copy of a server calculation
                is how the pickup fee once drifted a cent (see `dinerServiceFee`
                in ./pickup-cart). */}
            {taxLines.length > 0 && (
              <div className="mt-2 space-y-1" data-testid="receipt-tax-lines">
                <p className="text-xs text-muted-foreground">
                  {t("ui.react.features.user.receipt_screen.taxes_included")}
                </p>
                {taxLines.map((line, index) => (
                  <div
                    key={`${line.name}-${line.rate}`}
                    className="flex justify-between gap-3 text-xs text-muted-foreground"
                    data-testid={`bill-tax-line-${index}`}
                  >
                    <span className="min-w-0 truncate">
                      {t("ui.react.features.user.receipt_screen.tax_line", {
                        name: line.name,
                        rate: line.rate,
                      })}
                    </span>
                    <span className="shrink-0 tabular-nums">
                      {formatMoney(line.amount, { currency: bill.currency })}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* What came back, below the total it reduces -- and the figure the
                diner checks against their card statement. Rendered only when
                something was actually refunded: on an ordinary receipt the net
                is the total said twice.

                `netTotal` is the server's subtraction, not one done here. The
                pickup fee drifted a cent the last time a browser recomputed a
                server figure. */}
            {(bill.refunds ?? []).length > 0 && (
              <div className="mt-3 border-t border-dashed pt-3" data-testid="receipt-refunds">
                <p className="text-xs text-muted-foreground">
                  {t("ui.react.features.user.receipt_screen.refunds")}
                </p>
                {(bill.refunds ?? []).map((refund, index) => (
                  <div
                    key={`${refund.reason}-${refund.completedAt}-${index}`}
                    className="flex justify-between gap-3 text-xs text-muted-foreground"
                    data-testid={`receipt-refund-${index}`}
                  >
                    <span className="min-w-0 truncate">
                      {refund.completedAt
                        ? `${refund.reasonLabel} · ${formatDateTime(refund.completedAt, { timeZone: bill.timeZone })}`
                        : refund.reasonLabel}
                    </span>
                    <span className="shrink-0 tabular-nums">
                      −{formatMoney(refund.amount, { currency: bill.currency })}
                    </span>
                  </div>
                ))}
                <div className="mt-2 flex items-baseline justify-between gap-3 font-semibold">
                  <span className="text-sm">
                    {t("ui.react.features.user.receipt_screen.net_total")}
                  </span>
                  <span className="tabular-nums" data-testid="receipt-net-total">
                    {formatMoney(bill.netTotal ?? bill.total, { currency: bill.currency })}
                  </span>
                </div>
              </div>
            )}

            {/* How the money moved, below the figure it explains. Plural when a
                split settled across payers who did not pay the same way. */}
            {bill.paymentMethods.length > 0 && (
              <div
                className="mt-2 flex justify-between gap-3 text-xs text-muted-foreground"
                data-testid="receipt-payment-method"
              >
                <span>{t("ui.react.features.user.receipt_screen.payment_method")}</span>
                <span className="text-right">
                  {bill.paymentMethods.map((entry) => entry.label).join(" · ")}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* The entry to self-invoicing, and the whole reason the portal starts
            from the digital receipt rather than from a printed folio: the token
            travels in these props, so a diner taps once and types nothing they
            did not have to.

            Rendered off `invoiceToken` alone. The server sends it only when the
            portal would actually answer — the restaurant stamps CFDIs, the bill
            has settled, nobody has invoiced it and its month is still open — so
            there is no second opinion here to disagree with the screen behind
            the link. Never re-derive that from `bill.status` or `paidAt`. */}
        {bill.invoiceToken && (
          <Button
            asChild
            variant="outline"
            size="lg"
            className="mt-4 w-full"
            data-testid="receipt-invoice-action"
          >
            <Link href={`/invoice/${bill.invoiceToken}`}>
              <FileText />
              {t("ui.react.features.user.receipt_screen.request_invoice")}
            </Link>
          </Button>
        )}
      </div>
    </div>
  )
}

function SummaryLine({ label, value, currency }: { label: string; value: number; currency: string }) {
  return (
    <div className="flex justify-between py-0.5 text-sm text-muted-foreground">
      <span>{label}</span>
      <span className="tabular-nums">{formatMoney(value, { currency })}</span>
    </div>
  )
}
